// generic-client
import { useEffect, useRef } from 'react';
import qs from 'query-string';
import { isAfter } from 'date-fns';
import { useHistory, useLocation } from 'react-router-dom';
import { LOCAL_STORAGE } from '@constants';
import { safeUtmString } from 'utils/schemaUtils';
import JwtManager from 'lib/JwtManager';
import { addInteractionListener, removeInteractionListener } from 'utils/listenForInteraction';
import useMe from 'utils/hooks/useMe/useMe';
import buildLoginDestination from 'utils/buildLoginDestination';
import getValidatedParams from 'utils/getValidatedParams';

const useSession = () => {
  const history = useHistory();
  const location = useLocation<any>();

  const jwt = useRef(JwtManager.jwt);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = new Date();
      if (isAfter(now, JwtManager.idleExpiresAt)) {
        JwtManager.destroyJwt();
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const cb = () => JwtManager.resetIdleSessionExpirationDate();
    addInteractionListener(cb);
    return () => removeInteractionListener(cb);
  }, []);

  // User authentication flow
  const { refetch: meRefetch } = useMe({
    fetchPolicy: 'cache-only',
    skip: !jwt.current,
  });
  useEffect(() => {
    const jwtListener = (newJwt: string) => {
      if (jwt.current && newJwt && jwt.current !== newJwt) {
        // JWT update
        meRefetch();
      } else if (jwt.current && !newJwt) {
        // User logs out
        const destination = buildLoginDestination();
        history.push(destination);
      } else if (!jwt.current && newJwt) {
        // User logs in
        const destination = {
          pathname: '/',
          state: { from: window.location.pathname },
          search: '',
          hash: '',
        };

        // Guest redirection
        const { state } = window.history.state || {};
        if (state?.redirectPath) {
          destination.pathname = state.redirectPath;
        }
        if (state?.redirectSearch) {
          destination.search = state.redirectSearch;
        }
        if (state?.redirectHash) {
          destination.hash = state.redirectHash;
        }

        history.push(destination);
      }

      jwt.current = newJwt;
      window.ReactNativeWebView?.postMessage?.(JSON.stringify({ jwt: newJwt }));
    };
    JwtManager.addJwtListener(jwtListener);
    return () => JwtManager.removeJwtListener(jwtListener);
  }, []);

  // Browser Utm Data
  useEffect(() => {
    const search = qs.parse(location.search);
    const utmData = Object.entries(search).reduce((acc: any, [key, value]) => {
      if (key.startsWith('utm_')) {
        const validParams = getValidatedParams({
          params: { [key]: value },
          schema: { [key]: safeUtmString },
        });

        acc[key] = key in validParams ? validParams[key] : undefined;
      }
      return acc;
    }, {});
    if (Object.keys(utmData).length > 0) {
      sessionStorage.setItem(LOCAL_STORAGE.UTM, JSON.stringify(utmData));
    }
  }, [location.search]);
};

export default useSession;
